import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notificationsService } from '../services/notifications.service'
import type { NotificationFilters } from '../types/notification.types'

const QUERY_KEYS = {
  all: ['notifications'] as const,
  lists: () => [...QUERY_KEYS.all, 'list'] as const,
  list: (filters: NotificationFilters) => [...QUERY_KEYS.lists(), filters] as const,
  unreadCount: () => [...QUERY_KEYS.all, 'unread-count'] as const,
}

// Query: Buscar todas as notificações
export function useGetNotifications(filters: NotificationFilters = {}) {
  return useQuery({
    queryKey: QUERY_KEYS.list(filters),
    queryFn: () => notificationsService.getAll(filters),
    refetchInterval: 30000, // Refetch a cada 30 segundos
  })
}

// Query: Buscar contagem de não lidas
export function useGetUnreadCount() {
  return useQuery({
    queryKey: QUERY_KEYS.unreadCount(),
    queryFn: () => notificationsService.getUnreadCount(),
    refetchInterval: 30000, // Refetch a cada 30 segundos
  })
}

// Mutation: Marcar como lida
export function useMarkAsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (notificationIds: string[]) =>
      notificationsService.markAsRead(notificationIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.lists() })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.unreadCount() })
    },
  })
}

// Mutation: Marcar todas como lidas
export function useMarkAllAsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => notificationsService.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.lists() })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.unreadCount() })
    },
  })
}

// Mutation: Deletar notificação
export function useDeleteNotification() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => notificationsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.lists() })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.unreadCount() })
    },
  })
}

// Mutation: Deletar todas
export function useDeleteAllNotifications() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => notificationsService.deleteAll(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.lists() })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.unreadCount() })
    },
  })
}
